import React, { useState, useEffect } from 'react';

const AdminLeaves = ({ employees }) => {
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // form state
  const [employeeId, setEmployeeId] = useState('');
  const [type, setType] = useState('vacaciones');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');

  const fetchLeaves = async () => {
    try {
      const res = await fetch('/api/leaves');
      if (res.ok) {
        const data = await res.json();
        setLeaves(data);
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchLeaves();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const res = await fetch('/api/leaves', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: `leave-${Date.now()}`,
          employeeId,
          type,
          startDate,
          endDate,
          reason
        })
      });
      
      if (!res.ok) {
        throw new Error('Error al registrar el permiso');
      }
      
      // reset form
      setEmployeeId('');
      setReason('');
      setStartDate('');
      setEndDate('');
      fetchLeaves();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8 max-w-6xl mx-auto animate-fade-in-up">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Formulario */}
        <div className="glass-card p-6 rounded-2xl border-slate-800/80 h-fit">
          <h3 className="text-xl font-bold text-slate-100 mb-6 flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Registrar Permiso
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Empleado</label>
              <select 
                required
                value={employeeId} 
                onChange={e => setEmployeeId(e.target.value)}
                className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-xl text-slate-100 focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/25 transition-all text-sm"
              >
                <option value="">Seleccione un colaborador...</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.nombre} {emp.apellidos} - {emp.dni}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Tipo</label>
              <select 
                value={type} 
                onChange={e => setType(e.target.value)}
                className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-xl text-slate-100 focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/25 transition-all text-sm"
              >
                <option value="vacaciones">Vacaciones</option>
                <option value="permiso">Permiso Personal</option>
                <option value="descanso_medico">Descanso Médico</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Desde</label>
                <input 
                  type="date" 
                  required
                  value={startDate} 
                  onChange={e => setStartDate(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-xl text-slate-100 focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/25 transition-all text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Hasta</label>
                <input 
                  type="date" 
                  required
                  value={endDate} 
                  onChange={e => setEndDate(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-xl text-slate-100 focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/25 transition-all text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Motivo / Notas</label>
              <textarea 
                value={reason} 
                onChange={e => setReason(e.target.value)}
                className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-xl text-slate-100 focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/25 transition-all text-sm h-24 resize-none"
                placeholder="Especifique el motivo..."
              ></textarea>
            </div>
            
            {error && <p className="text-rose-400 text-sm">{error}</p>}
            
            <button 
              type="submit" 
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-400 hover:to-indigo-500 text-white font-semibold rounded-xl shadow-lg shadow-purple-500/20 transition-all duration-200"
            >
              {loading ? 'Guardando...' : 'Registrar Permiso'}
            </button>
          </form>
        </div>

        {/* Tabla */}
        <div className="lg:col-span-2 glass-card p-6 rounded-2xl border-slate-800/80">
          <h3 className="text-xl font-bold text-slate-100 mb-6">Historial de Permisos</h3>
          
          {leaves.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              No hay permisos registrados.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-slate-800 text-slate-400 text-xs uppercase tracking-wider">
                    <th className="p-4 font-medium">Empleado</th>
                    <th className="p-4 font-medium">Tipo</th>
                    <th className="p-4 font-medium">Fechas</th>
                    <th className="p-4 font-medium">Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/50">
                  {leaves.map((leave) => (
                    <tr key={leave.id} className="hover:bg-slate-800/20 transition-colors">
                      <td className="p-4 text-sm font-medium text-slate-200">{leave.employeeName}</td>
                      <td className="p-4 text-sm">
                        <span className={`px-2 py-1 rounded-md text-xs font-medium capitalize ${
                          leave.type === 'vacaciones' ? 'bg-amber-500/10 text-amber-400' :
                          leave.type === 'descanso_medico' ? 'bg-blue-500/10 text-blue-400' :
                          'bg-purple-500/10 text-purple-400'
                        }`}>
                          {leave.type.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="p-4 text-sm text-slate-300 font-mono">
                        {leave.startDate} al {leave.endDate}
                      </td>
                      <td className="p-4 text-sm">
                        <span className="px-2 py-1 bg-emerald-500/10 text-emerald-400 rounded-md text-xs font-medium">
                          {leave.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        
      </div>
    </div>
  );
};

export default AdminLeaves;
